// @ts-check
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import Button from '@liquidweb-dev/undercurrent/Button';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import EllipsisIcon from '@liquidweb-dev/undercurrent/icons/lucide/Ellipsis';
import HeadsetIcon from '@liquidweb-dev/undercurrent/icons/lucide/Headset';
import TrashIcon from '@liquidweb-dev/undercurrent/icons/lucide/Trash2';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import DrawerSupportAccess from 'components/standard/DrawerSupportAccess';
import ActionsContainer from 'components/standard/HostingDetailsActions/ActionsContainer';
import ActionsLoader from 'components/standard/HostingDetailsActions/ActionsLoader';
import FavoriteMenuItem from 'components/standard/HostingDetailsActions/FavoriteMenuItem';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useVmWareVCloudVdcLoginUrl from 'modules/queries/vmWare/vCloud/vdc/useLogin';

/**
 * @typedef {'' | 'supportAccess'} ActionKey
 * */

const SArrowUpRight = () => (
	<ArrowUpRightIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SHeadset = () => (
	<HeadsetIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const STrash = () => (
	<TrashIcon
		sx={(theme) => ({
			height: theme.size.xS,
			width: theme.size.xS,
			color: theme.vars.palette.uc.danger.icon.main,
		})}
	/>
);

/** @param {import('modules/queries/asset/useDetails').AssetDetails} props */
const ActionsContent = ({ capabilities, domain, status, uniq_id: uniqId }) => {
	const history = useHistory();

	const [menuAnchorEl, setMenuAnchorEl] = useState(
		/** @type {HTMLButtonElement | null} */ (null),
	);
	const [actionContentKey, setActionContentKey] = useState(
		/** @type {ActionKey} */ (''),
	);

	const { mutateAsync: fetchVmWareLogin } = useVmWareVCloudVdcLoginUrl();

	const isActive = status === 'Active';

	const showGoToSupportAccess = !fromApiBoolean(
		capabilities?.credentialsNotCustomerFacing,
	);
	const showDestroyServer = isActive;

	const hasTopGroup = showGoToSupportAccess;
	const hasBottomGroup = showDestroyServer;

	const handleVmWareVCloudVdcLogin = async () => {
		try {
			const data = await fetchVmWareLogin({ uniq_id: uniqId });

			if (data.url) {
				window.open(data.url, '_blank', 'noopener');
			}
		} catch {
			// handled in hook definition
		}
	};

	/** @param {ActionKey} key */
	const handleMenuItemClick = (key) => {
		setMenuAnchorEl(null);
		setActionContentKey(key);
	};

	return (
		<ActionsContainer>
			<Button
				variant="primary"
				size="large"
				onClick={handleVmWareVCloudVdcLogin}
				sx={{
					width: { xs: '100%', md: 'auto' },
					// one-off colors for vmware login button
					backgroundColor: '#E6F6FF',
					color: '#194F99',
					'&:hover': {
						backgroundColor: '#194F99',
						color: '#E6F6FF',
					},
				}}
				startIcon={<SArrowUpRight />}
			>
				Cloud Director
			</Button>
			<IconButton
				variant="secondary"
				size="large"
				onClick={(event) => setMenuAnchorEl(event.currentTarget)}
				sx={{ flexShrink: 0 }}
			>
				<EllipsisIcon />
			</IconButton>

			<Menu
				open={Boolean(menuAnchorEl)}
				onClose={() => setMenuAnchorEl(null)}
				anchorEl={menuAnchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				{showGoToSupportAccess && (
					<MenuItem
						disabled={!isActive}
						onClick={() => handleMenuItemClick('supportAccess')}
					>
						<ListItemIcon>
							<SHeadset />
						</ListItemIcon>
						<Typography variant="label-lg">Support access</Typography>
					</MenuItem>
				)}

				{hasTopGroup && <Divider sx={{ marginX: { sm: -50 } }} />}

				<FavoriteMenuItem
					onClick={() => setMenuAnchorEl(null)}
					favoritesDisplayName={domain}
					uniqId={uniqId}
				/>

				{hasBottomGroup && <Divider sx={{ marginX: { sm: -50 } }} />}

				{showDestroyServer && (
					<MenuItem
						onClick={() => {
							setMenuAnchorEl(null);
							history.push(`/servers/${uniqId}/settings`);
						}}
						sx={{
							':hover, :focus-visible': {
								backgroundColor: ({ vars }) => vars.palette.uc.danger.bg.main,
							},
						}}
					>
						<ListItemIcon>
							<STrash />
						</ListItemIcon>
						<Typography
							variant="label-lg"
							sx={{ color: ({ vars }) => vars.palette.uc.danger.icon.strong }}
						>
							Destroy server
						</Typography>
					</MenuItem>
				)}
			</Menu>

			<DrawerSupportAccess
				open={actionContentKey === 'supportAccess'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
		</ActionsContainer>
	);
};

const Actions = () => {
	/** @type { {uniqId: string} } */
	const { uniqId } = useParams();
	const { data, isLoading, isError } = useAssetDetails(
		{
			alsowith: hostingDetailsAlsowith,
			uniq_id: uniqId,
		},
		{ enabled: Boolean(uniqId) },
	);

	if (isError) {
		return null;
	}

	if (isLoading) {
		return <ActionsLoader />;
	}

	return <ActionsContent {...data} />;
};

export default Actions;
